// client/src/pages/AdminSellerApplications.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip, Button, Stack,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const statusColor = { pending: 'warning', approved: 'success', rejected: 'error' };

const AdminSellerApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const { token } = useContext(AuthContext);

  const fetchApplications = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/seller-applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch applications. You may not have permission.');
      setApplications(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this application? The applicant will become a seller.')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/seller-applications/${id}/approve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to approve application.');
      fetchApplications();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReject = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/seller-applications/${rejectTarget.id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ adminNotes }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to reject application.');
      setRejectTarget(null);
      setAdminNotes('');
      fetchApplications();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Seller Applications</Typography>
      {applications.length === 0 ? (
        <EmptyState icon={<HowToRegOutlinedIcon />} title="No applications yet" description="Customer applications to become a seller will appear here for review." />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Applicant</TableCell><TableCell>Store Name</TableCell><TableCell>Description</TableCell>
                <TableCell>Phone</TableCell><TableCell>Submitted</TableCell><TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((app) => (
                <TableRow key={app.id} hover>
                  <TableCell>{app.applicant_email}</TableCell>
                  <TableCell>{app.store_name}</TableCell>
                  <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal' }}>{app.description}</TableCell>
                  <TableCell>{app.contact_phone || '—'}</TableCell>
                  <TableCell>{new Date(app.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><Chip label={app.status} size="small" color={statusColor[app.status]} sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell align="right">
                    {app.status === 'pending' && (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="contained" color="success" onClick={() => handleApprove(app.id)}>Approve</Button>
                        <Button size="small" color="error" onClick={() => setRejectTarget(app)}>Reject</Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(rejectTarget)} onClose={() => setRejectTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reject application from {rejectTarget?.applicant_email}</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason (optional, shown to the applicant)"
            fullWidth multiline minRows={3} sx={{ mt: 1 }}
            value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Reject Application</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSellerApplications;
